import { Left, Right, Either } from './../../utils/either';
import { UnknownNetworkError } from './../../application/commonModels/apiResponse';
import {
  GoogleRegisterError,
  GoogleRegisterApiSuccess,
  GoogleConfirmEmailAlreadyRegisteredError,
  GoogleConfirmResult,
  GoogleConfirmGeneralError,
  GoogleConfirmErrorTypes
} from './../../application/usecases/googleInteractor';
import { AxiosResponse } from 'axios';
import ApiClient from './client';
import { Option } from '../../utils/option';
import { GoogleAccessToken } from '../../domain/google';
import {
  SecureUserErrorData,
  SecureUserData,
  getSecureUserErrorContent
} from './models';
import { convertWaServiceToTrackerRegisteredVia } from '../../utils/registeredVia';
import { WA_SERVICE } from '../../domain/waService';

const register = async (
  googleAccessToken: GoogleAccessToken,
  waService: WA_SERVICE
) => {
  const res = await ApiClient.instance.post<SecureUserData>(
    '/auth/google/registrations',
    {
      secure_user: {
        secure_google_account: {
          token: googleAccessToken
        }
      },
      registered_via: convertWaServiceToTrackerRegisteredVia(waService)
    },
    'wapi'
  );

  return res
    .leftMap((e) =>
      Option<AxiosResponse<SecureUserErrorData>>(e.response)
        .flatMap(({ data }) =>
          getSecureUserErrorContent(Option(data)).map(
            (error) => new GoogleRegisterError(error)
          )
        )
        .getOrElse<GoogleRegisterError | UnknownNetworkError>(
          () => new UnknownNetworkError()
        )
    )
    .map(({ data, status }): GoogleRegisterApiSuccess => {
      const { confirmed_at, email } = data.secure_user;
      return {
        sessionType:
          status === 201 || confirmed_at == null ? 'signup' : 'signin',
        accessToken: data.secure_user.access_token,
        refreshToken: data.secure_user.refresh_token,
        userId: data.user.id,
        googleAccessToken,
        googleEmail: email
      };
    });
};

const confirm = async (email: string, token: string) => {
  const res = await ApiClient.instance.patch<SecureUserData>(
    '/auth/google/confirmations',
    {
      secure_user: {
        email,
        secure_google_account: {
          token
        }
      }
    },
    'wapi'
  );

  return res
    .map<GoogleConfirmResult>(() => ({ type: 'emailSameAsGmail' }))
    .leftFlatMap(
      (e): Either<GoogleConfirmErrorTypes, GoogleConfirmResult> =>
        Option<AxiosResponse<SecureUserErrorData>>(e.response)
          .toEither(new UnknownNetworkError())
          .flatMap(
            ({
              status,
              data
            }): Either<GoogleConfirmErrorTypes, GoogleConfirmResult> => {
              switch (status) {
                case 409:
                  return new Right<
                    GoogleConfirmErrorTypes,
                    GoogleConfirmResult
                  >({ type: 'emailDifferentFromGmail' });
                case 403: // googleから取得したメアドをそのまま入力したがすでに他のユーザがそのメアドを登録されている場合
                  return new Left(
                    new GoogleConfirmEmailAlreadyRegisteredError()
                  );
                case 401:
                  return new Left(new UnknownNetworkError());
                default:
                  return getSecureUserErrorContent(Option(data))
                    .toEither(new UnknownNetworkError())
                    .flatMap(
                      (error) => new Left(new GoogleConfirmGeneralError(error))
                    );
              }
            }
          )
    );
};

export default {
  confirm,
  register
};
